'use client';

import { useEffect } from 'react';
import { useFormatter, useTranslations } from 'next-intl';
import { CalendarX, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Link, useRouter } from '@/i18n/routing';
import { useLicenseStatus } from '../hooks/use-license-status';
import { LicenseKeyForm } from './license-key-form';

export function LicenseExpiredView() {
  const t = useTranslations('license');
  const format = useFormatter();
  const router = useRouter();
  const { data, refetch, isFetching } = useLicenseStatus();

  // Self-heal: when the license is valid (renewed, or someone navigated here
  // directly), leave for the login page.
  useEffect(() => {
    if (data && !data.expired) {
      router.replace('/login');
    }
  }, [data, router]);

  async function handleRecheck() {
    const result = await refetch();
    if (result.data && !result.data.expired) {
      router.replace('/login');
    }
  }

  const expiredOn = data?.expires_at
    ? format.dateTime(new Date(data.expires_at), { dateStyle: 'long' })
    : null;

  return (
    <div className='bg-background flex min-h-screen items-center justify-center p-4'>
      <Card className='w-full max-w-md'>
        <CardContent className='flex flex-col gap-4 p-8'>
          <div className='flex flex-col items-center gap-4 text-center'>
            <div className='bg-destructive/10 rounded-full p-4'>
              <CalendarX className='text-destructive h-10 w-10' />
            </div>
            <h1 className='text-xl font-semibold'>{t('title')}</h1>
            <p className='text-muted-foreground text-sm'>{t('description')}</p>
            {expiredOn ? (
              <p className='text-muted-foreground text-xs'>
                {t('expiredOn', { date: expiredOn })}
              </p>
            ) : null}
          </div>

          <div className='border-t pt-4'>
            <LicenseKeyForm />
          </div>

          <div className='flex items-center justify-center gap-2 border-t pt-4'>
            <Button
              size='sm'
              variant='outline'
              onClick={handleRecheck}
              disabled={isFetching}
            >
              <RefreshCw
                className={cn('me-2 h-4 w-4', isFetching && 'animate-spin')}
              />
              {t('recheck')}
            </Button>
            <Button size='sm' variant='ghost' asChild>
              <Link href='/login'>{t('backToLogin')}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
